import { QueryClient, useQuery, useQueryClient } from 'react-query';
import { server as StardogServer } from 'stardog';
import { BaseConnection, getStardogConnection } from 'vet-bones/utils';

import { Connection as GraphConnection } from 'src/ui/graph/types';
import { previousConnectionData } from 'src/ui/hooks/previousConnectionData';
import { runWithRefetchConnectionToken } from 'src/ui/hooks/refetchConnectionToken';

/** Shape of `/admin/status` — a flat map of metric name to value. */
export type ServerStatus = Record<string, { value?: unknown } | undefined>;

const serverStatusKeyPrefix = (connectionId: string): unknown[] => [
  'connectionServerStatus',
  connectionId,
];

/**
 * Server version and license limits don't change within a session, and the key
 * already changes when the connection is re-authenticated — so there's nothing
 * for a focus refetch to pick up.
 */
const SERVER_STATUS_QUERY_OPTIONS = {
  refetchOnWindowFocus: false,
  staleTime: Infinity,
} as const;

const fetchServerStatus = async (
  connection: GraphConnection,
  org: string
): Promise<ServerStatus | null> => {
  const stardogConnection = getStardogConnection(connection as BaseConnection, {
    agent: 'Portal',
  });

  try {
    const response = await runWithRefetchConnectionToken(org)(
      stardogConnection,
      connection.index,
      (stardogConn) => StardogServer.status(stardogConn, { databases: false })
    );
    if (!response.ok) {
      console.error('Failed to get endpoint status: ', {
        connectionId: connection.id,
        status: response.status,
        statusText: response.statusText,
      });
      return null;
    }
    return (response.body as ServerStatus) ?? null;
  } catch (err) {
    console.error(`problem requesting connection server status: ${err}`);
    return null;
  }
};

/**
 * Query config for a connection's server status. Exported so `useQueries`
 * callers build the *same* keys as `useConnectionServerStatus` and share its
 * cache entries.
 */
export const serverStatusQueryConfig = (
  queryClient: QueryClient,
  connection: GraphConnection,
  org: string
) => {
  const keyPrefix = serverStatusKeyPrefix(connection.id);
  return {
    queryFn: () => fetchServerStatus(connection, org),
    queryKey: [...keyPrefix, connection.token ?? null],
    ...SERVER_STATUS_QUERY_OPTIONS,
    placeholderData: () =>
      previousConnectionData<ServerStatus | null>(queryClient, keyPrefix),
  };
};

/**
 * One `/admin/status` request per connection, shared by every consumer that
 * needs server metadata (version, license limits).
 *
 * `useEndpointVersion`, `useIsVoiceboxEnabledServer`, and `useNavbarSupport`
 * each used to issue this identical request independently — the HAR for
 * VET-7231 showed `/admin/status` ten times in one short session. They now all
 * read this single query, so the cache dedupes them.
 *
 * This doubles as the connection's auth probe: if it succeeds, the token works.
 * Consumers derive from its data rather than fetching with their own tokens, so
 * the token only has to appear in *this* key.
 */
export const useConnectionServerStatus = (
  connection: GraphConnection | null | undefined,
  org: string
) => {
  const queryClient = useQueryClient();
  const keyPrefix = serverStatusKeyPrefix(connection?.id ?? 'unknown');

  return useQuery<ServerStatus | null>(
    [...keyPrefix, connection?.token ?? null],
    () =>
      connection ? fetchServerStatus(connection, org) : Promise.resolve(null),
    {
      ...SERVER_STATUS_QUERY_OPTIONS,
      enabled: Boolean(connection),
      placeholderData: () =>
        previousConnectionData<ServerStatus | null>(queryClient, keyPrefix),
    }
  );
};

/** Coerced `dbms.version`, or null when the status call failed. */
export const serverVersionFromStatus = (
  status: ServerStatus | null | undefined
): string | null => {
  const version = status?.['dbms.version']?.value;
  return typeof version === 'string' ? version : null;
};

/** Voicebox license seat limit, 0 when absent. */
export const voiceboxLimitFromStatus = (
  status: ServerStatus | null | undefined
): number => {
  const limit = status?.['dbms.license.voicebox.limit']?.value;
  return typeof limit === 'number' ? limit : 0;
};
